import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import SoftBadge from 'components/common/SoftBadge';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { format } from 'date-fns';
import { useDonorsWithRemainingDonations } from 'hooks/useBeneficiaries';
import { formatMoney } from 'helpers/utils';

const SelectDonationModal = ({ show, onHide, onSubmit, initialId }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const { donorsWithDonations, isLoading: loading } =
    useDonorsWithRemainingDonations({ includeAll: true });

  useEffect(() => {
    if (initialId) setSelectedId(initialId);
  }, [show, initialId]);

  useEffect(() => {
    if (!show) setSearchTerm('');
  }, [show]);

  const tableData = useMemo(() => {
    const rows = (donorsWithDonations || []).map(d => {
      const donorName =
        `${d.donor?.first_name || ''} ${d.donor?.last_name || ''}`.trim() ||
        '-';
      return {
        id: d.orderDetailId,
        ref: d.donor?.simple_id ? `D${d.donor.simple_id}` : '',
        donorName,
        donationType: d.donationType || '-',
        package: d.sponsorshipType || '-',
        amount: d.amount,
        remaining: d.remainingAmount,
        billingCondition: d.billingCondition || '-',
        createdAt: d.createdAt,
        raw: d
      };
    });

    const term = searchTerm.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(r =>
      [r.ref, r.donorName, r.donationType, r.package, r.billingCondition]
        .some(v => v && String(v).toLowerCase().includes(term))
    );
  }, [donorsWithDonations, searchTerm]);

  const columns = [
    {
      accessor: 'ref',
      Header: 'Donor Ref',
      cellProps: { className: 'fw-semi-bold py-2' }
    },
    {
      accessor: 'donorName',
      Header: 'Donor',
      cellProps: { className: 'fw-medium py-2' }
    },
    {
      accessor: 'package',
      Header: 'Package',
      cellProps: { className: 'py-2' }
    },
    {
      accessor: 'donationType',
      Header: 'Type',
      cellProps: { className: 'py-2' }
    },
    {
      accessor: 'billingCondition',
      Header: 'Billing',
      cellProps: { className: 'py-2' },
      Cell: rowData => {
        const v = rowData.row.original.billingCondition;
        if (!v || v === '-') return <>&mdash;</>;
        return v.charAt(0).toUpperCase() + v.slice(1);
      }
    },
    {
      accessor: 'amount',
      Header: 'Amount',
      cellProps: { className: 'py-2 text-end' },
      Cell: rowData => formatMoney(rowData.row.original.amount)
    },
    {
      accessor: 'remaining',
      Header: 'Remaining',
      cellProps: { className: 'py-2 text-end' },
      Cell: rowData => {
        const r = rowData.row.original.remaining;
        if (r > 0) {
          return (
            <span className="text-success fw-semi-bold">{formatMoney(r)}</span>
          );
        }
        return (
          <SoftBadge pill bg="secondary">
            Fully allocated
          </SoftBadge>
        );
      }
    },
    {
      accessor: 'createdAt',
      Header: 'Date',
      cellProps: { className: 'py-2' },
      Cell: rowData => {
        const v = rowData.row.original.createdAt;
        return v ? format(new Date(v), 'dd MMM yyyy') : <>&mdash;</>;
      }
    }
  ];

  const handleSelectRow = id => {
    setSelectedId(prev => (prev === id ? null : id));
  };

  const selectedRow = tableData.find(r => r.id === selectedId);
  const selectedHasRemaining = !!selectedRow && selectedRow.remaining > 0;

  const onNext = () => {
    if (!selectedRow) return;
    onSubmit(selectedRow.raw);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="border-0">
        <Modal.Title as="h5">Select Donation</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          <Col xs={12}>
            <Form.Control
              type="text"
              autoFocus
              placeholder="Search by donor ref, name, package, type or billing..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </Col>
          <Col xs={12} className="d-flex flex-column">
            <div className="border rounded-3 overflow-hidden">
              <AdvanceTableWrapper
                columns={columns}
                data={tableData}
                sortable
                pagination
                perPage={10}
              >
                <AdvanceTable
                  table
                  headerClassName="bg-200 text-900 text-nowrap align-middle"
                  rowClassName="align-middle white-space-nowrap"
                  tableProps={{
                    striped: true,
                    className: 'fs--1 mb-0 overflow-hidden'
                  }}
                  loading={loading}
                  selectableRow
                  selectedRowId={selectedId}
                  onSelectRow={handleSelectRow}
                />
                <div className="p-3">
                  <AdvanceTableFooter
                    rowCount={tableData.length}
                    table
                    rowInfo
                    navButtons
                  />
                </div>
              </AdvanceTableWrapper>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-3">
              {selectedRow && !selectedHasRemaining ? (
                <small className="text-warning">
                  This donation is fully allocated and can't fund new
                  sponsorship coverage.
                </small>
              ) : (
                <span />
              )}
              <Button
                size="lg"
                className="px-7"
                disabled={!selectedRow || !selectedHasRemaining}
                onClick={onNext}
              >
                Next
              </Button>
            </div>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
};

SelectDonationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialId: PropTypes.string
};

export default SelectDonationModal;
